import { ActionIcon, Box, Center, Flex, Stack, Text } from '@mantine/core';
import { IconEdit as EditIcon, IconRefresh } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { getEvaluationItemFileDownloadUrl } from '@/module/Evaluation/model/EvaluationApi';
import { useEvaluationDeleteCriteriaItem } from '@/module/Evaluation/model/EvaluationQuery';
import { EvaluationCriteriaDataItem } from '@/module/Evaluation/model/EvaluationTypes';

import { Content, DeleteButton } from '@/core/components';

import { ExternalSourceLink, FileCard, Id, useAppNotification } from '@feature/utils';

import { EvaluationItemCardHeader } from './components/card/EvaluationItemCardHeader';

interface EvaluationCriteriaItemCardProps {
  item: EvaluationCriteriaDataItem;
  index: number;

  evaluationId: Id;
  authorId: Id;
  orderNo: number;
  dataEntryHint: string | null;

  onEdit: (item: EvaluationCriteriaDataItem) => void;
  onRecalculate: (item: EvaluationCriteriaDataItem) => void;
}

function useItemDelete(evaluationId: Id, authorId: Id, orderNo: number) {
  const { mutateAsync, isPending } = useEvaluationDeleteCriteriaItem(evaluationId, authorId, orderNo);
  const notification = useAppNotification();
  const onDelete = (itemId: Id) => {
    return mutateAsync(itemId).then(notification.success).catch(notification.error);
  };

  return {
    onDelete,
    isDeleting: isPending,
  };
}

export function EvaluationCriteriaItemCard({
  evaluationId,
  authorId,
  orderNo,
  item,
  index,
  onEdit,
  dataEntryHint,
  onRecalculate,
}: EvaluationCriteriaItemCardProps) {
  const { onDelete, isDeleting } = useItemDelete(evaluationId, authorId, orderNo);
  const { t } = useTranslation();

  return (
    <Content
      flex={1}
      type="full"
    >
      <Flex justify="space-between">
        <Flex flex={1}>
          <Stack
            gap="2px"
            justify="center"
            align="center"
            mr="10px"
          >
            {index != null && <Text fw="500">#{index + 1}</Text>}
            <Text
              c="dimmed"
              fw="500"
              size="xs"
            >
              ({item.points} pct)
            </Text>
            <ActionIcon
              onClick={() => onEdit(item)}
              variant="filled"
              aria-label={t('button.edit')}
            >
              <EditIcon
                style={{ width: '70%', height: '70%' }}
                stroke={1.5}
              />
            </ActionIcon>
            <ActionIcon
              onClick={() => onRecalculate(item)}
              variant="filled"
              aria-label={t('button.refresh')}
            >
              <IconRefresh
                style={{ width: '70%', height: '70%' }}
                stroke={1.5}
              />
            </ActionIcon>
          </Stack>
          <Stack
            flex={1}
            gap="4px"
            mr="12px"
          >
            <EvaluationItemCardHeader
              item={item}
              dataEntryHint={dataEntryHint}
            />
            <Flex
              flex={1}
              justify="space-between"
            >
              <Stack gap="4px">
                {item.link && (
                  <ExternalSourceLink
                    url={item.link}
                    name={item.link}
                  />
                )}
                <Text
                  c="dimmed"
                  size="sm"
                  fs="italic"
                >
                  {item.note}
                </Text>
              </Stack>
              <Box
                mr="15px"
                maw="180px"
              >
                {item.discriminator != null && (
                  <Text
                    c="dimmed"
                    size="sm"
                    td="underline"
                  >
                    {item.discriminator.name}
                  </Text>
                )}
              </Box>
            </Flex>
            <FileCard
              file={item.file}
              onDownload={() => window.open(getEvaluationItemFileDownloadUrl(evaluationId, authorId, orderNo, item.id))}
            />
          </Stack>
        </Flex>
        <Center>
          <DeleteButton
            size="xs"
            onDelete={() => onDelete(item.id)}
            isDeleting={isDeleting}
          />
        </Center>
      </Flex>
    </Content>
  );
}
